import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import AdminAPI, {
  getRegionMap,
  getRegions,
  getEnemies,
  getEnemyTypes,
  getLootContainerTypes,
  getSpawnableMapItems,
  getTileDecorations,
  getTileTypes,
  previewRegionResourceSpawns,
  previewRegionLootContainerSpawns,
  previewRegionEnemySpawns,
  saveRegionMap,
} from '../AdminAPI';
import RegionMapEditor from './RegionMapEditor';
import RegionExitEditor from './RegionExitEditor';
import ResourceSpawnRulesEditor from './ResourceSpawnRulesEditor';
import LootContainerRulesEditor from './LootContainerRulesEditor';
import EnemySpawnPreviewEditor from './EnemySpawnPreviewEditor';
import { TILE_KIND_BASE, TILE_KIND_DETAIL } from '../../GameWorld/tiles';
import { getDefaultBaseTiles, getDefaultDetailTiles, NONE_DETAIL_OPTION, normalizeTileData } from '../../GameWorld/tileUtils';
import useImageCache from '../../../hooks/useImageCache';
import { MEDIA_VARIANTS } from '../../../media';
import {
  ensureRegionMap,
  mapPayload,
  mergeSavedMapVersion,
  nextMapEntryKey,
  normalizeLootContainerSpawnRule,
  normalizeResourcePlacement,
  normalizeResourceSpawnRule,
  resizeMap,
  resizeMapEdge,
} from './worldEditorModel';
import '../Admin.css';
import './WorldEditor.css';

function field(source, camelName, pascalName) {
  return source?.[camelName] ?? source?.[pascalName];
}

const SIDEBAR_TABS = [
  { id: 'terrain', label: 'Terrain' },
  { id: 'travel', label: 'Travel' },
  { id: 'enemies', label: 'Enemies' },
  { id: 'resources', label: 'Resources' },
  { id: 'loot', label: 'Loot' },
];

function AdminWorldEditor() {
  const [regions, setRegions] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState('');
  const [map, setMap] = useState(() => ensureRegionMap(null, 10, 10));
  const [activeBaseTile, setActiveBaseTile] = useState('sand');
  const [activeDetail, setActiveDetail] = useState('none');
  const [activeWalkableOverride, setActiveWalkableOverride] = useState(null);
  const [paintLayer, setPaintLayer] = useState('base');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [tileTypes, setTileTypes] = useState(() => getDefaultBaseTiles());
  const [detailOptions, setDetailOptions] = useState(() => getDefaultDetailTiles());
  const [spawnableItems, setSpawnableItems] = useState([]);
  const [spawnableItemsError, setSpawnableItemsError] = useState('');
  const [placementMode, setPlacementMode] = useState('');
  const [resourcePreview, setResourcePreview] = useState(null);
  const [lootContainerTypes, setLootContainerTypes] = useState([]);
  const [lootPreview, setLootPreview] = useState(null);
  const [lootPreviewError, setLootPreviewError] = useState('');
  const [isPreviewingLoot, setIsPreviewingLoot] = useState(false);
  const [enemies, setEnemies] = useState([]);
  const [enemyTypes, setEnemyTypes] = useState([]);
  const [enemyCatalogError, setEnemyCatalogError] = useState('');
  const [enemyPreview, setEnemyPreview] = useState(null);
  const [enemyPreviewError, setEnemyPreviewError] = useState('');
  const [isPreviewingEnemies, setIsPreviewingEnemies] = useState(false);
  const [selectedEnemyId, setSelectedEnemyId] = useState('');
  const [selectedEnemyInstanceId, setSelectedEnemyInstanceId] = useState('');
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [activeSidebarTab, setActiveSidebarTab] = useState('terrain');
  const [showFogPreview, setShowFogPreview] = useState(false);
  const [showWalkabilityOverlay, setShowWalkabilityOverlay] = useState(false);
  const selectedRegionIdRef = useRef('');
  const editVersionRef = useRef(0);

  const fetchImage = useCallback((imageId, variant) => AdminAPI.fetchImage(imageId, variant), []);
  const getImageUrl = useImageCache(fetchImage);

  const markDirty = () => {
    editVersionRef.current += 1;
    setIsDirty(true);
    setResourcePreview(null);
    setLootPreview(null);
    setEnemyPreview(null);
    setPreviewError('');
    setLootPreviewError('');
    setEnemyPreviewError('');
    setSelectedEnemyId('');
    setSelectedEnemyInstanceId('');
    setSaveFeedback(null);
  };

  useEffect(() => {
    selectedRegionIdRef.current = selectedRegionId;
  }, [selectedRegionId]);

  useEffect(() => {
    async function loadRegions() {
      const list = await getRegions();
      if (Array.isArray(list)) {
        setRegions(list);
        setSelectedRegionId((prev) => {
          const nextRegionId = prev || field(list[0], 'id', 'Id') || '';
          selectedRegionIdRef.current = nextRegionId;
          return nextRegionId;
        });
      }
    }
    loadRegions();
  }, []);

  useEffect(() => {
    Promise.all([getEnemies(), getEnemyTypes()])
      .then(([enemyList, typeList]) => {
        setEnemies(Array.isArray(enemyList) ? enemyList : []);
        setEnemyTypes(Array.isArray(typeList) ? typeList : []);
        setEnemyCatalogError('');
      })
      .catch(error => setEnemyCatalogError(
        error?.message || 'The enemy roster could not be loaded.',
      ));
  }, []);

  useEffect(() => {
    getLootContainerTypes()
      .then(list => setLootContainerTypes(Array.isArray(list) ? list : []))
      .catch(error => setLootPreviewError(
        error?.message || 'Loot container types could not be loaded.',
      ));
  }, []);

  useEffect(() => {
    async function loadTiles() {
      const list = await getTileTypes({ type: TILE_KIND_BASE });
      if (Array.isArray(list) && list.length) {
        const normalized = list
          .map((tile) => normalizeTileData(tile, TILE_KIND_BASE))
          .filter((tile) => tile && tile.type === TILE_KIND_BASE);
        if (normalized.length) {
          setTileTypes(normalized);
        }
      }
    }
    loadTiles();
  }, []);

  useEffect(() => {
    async function loadDecorations() {
      const list = await getTileDecorations();
      if (Array.isArray(list) && list.length) {
        const normalized = list
          .map((detail) => normalizeTileData(detail, TILE_KIND_DETAIL))
          .filter((detail) => detail && detail.type === TILE_KIND_DETAIL);
        if (normalized.length) {
          setDetailOptions([NONE_DETAIL_OPTION, ...normalized]);
        }
      }
    }
    loadDecorations();
  }, []);

  useEffect(() => {
    async function loadSpawnableItems() {
      try {
        const items = await getSpawnableMapItems();
        setSpawnableItems(Array.isArray(items) ? items : []);
        setSpawnableItemsError('');
      } catch (error) {
        setSpawnableItemsError(
          error?.message || 'Spawnable items could not be loaded.',
        );
      }
    }
    loadSpawnableItems();
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadMap() {
      if (!selectedRegionId) return;
      setIsLoadingMap(true);
      setSaveFeedback(null);
      try {
        const fetched = await getRegionMap(selectedRegionId);
        if (cancelled) return;
        const nextMap = fetched
          ? ensureRegionMap(
            fetched,
            field(fetched, 'width', 'Width'),
            field(fetched, 'height', 'Height'),
          )
          : ensureRegionMap(null, 10, 10);
        setMap(nextMap);
        editVersionRef.current += 1;
        setIsDirty(false);
        setPlacementMode('');
        setResourcePreview(null);
        setLootPreview(null);
        setEnemyPreview(null);
        setPreviewError('');
        setLootPreviewError('');
        setEnemyPreviewError('');
        setSelectedEnemyId('');
        setSelectedEnemyInstanceId('');
      } catch (error) {
        if (cancelled) return;
        setSaveFeedback({
          type: 'error',
          message: error?.message || 'The region map could not be loaded.',
        });
      } finally {
        if (!cancelled) setIsLoadingMap(false);
      }
    }
    loadMap();
    return () => {
      cancelled = true;
    };
  }, [selectedRegionId]);

  const handlePaint = (
    x,
    y,
    baseOverride,
    detailOverride,
    walkableOverride,
  ) => {
    setMap((prev) => {
      const nextTiles = prev.tiles.map((row, rowIdx) => {
        if (rowIdx !== y) return row;
        return row.map((tile, colIdx) => {
          if (colIdx !== x) return tile;
          if (paintLayer === 'base') {
            return { ...tile, base: baseOverride || activeBaseTile };
          }
          if (paintLayer === 'detail') {
            const detail = detailOverride || activeDetail;
            return { ...tile, detail: detail === 'none' ? null : detail };
          }
          return { ...tile, walkableOverride };
        });
      });
      return { ...prev, tiles: nextTiles };
    });
    markDirty();
  };

  const handleResize = (nextWidth, nextHeight) => {
    const width = Math.max(1, Math.min(200, parseInt(nextWidth, 10) || 1));
    const height = Math.max(1, Math.min(200, parseInt(nextHeight, 10) || 1));
    setMap(prev => resizeMap(prev, width, height));
    setPlacementMode('');
    markDirty();
  };

  const handleResizeMapEdge = (edge, amount) => {
    setMap(prev => resizeMapEdge(prev, edge, amount));
    setPlacementMode('');
    markDirty();
  };

  const handleSave = async () => {
    if (!selectedRegionId) return;
    const saveRegionId = selectedRegionId;
    const saveEditVersion = editVersionRef.current;
    const payload = mapPayload(map);
    setIsSaving(true);
    setSaveFeedback(null);
    try {
      const selectedRegion = regions.find(region => (
        field(region, 'id', 'Id') === saveRegionId
      ));
      if (!selectedRegion) {
        throw new Error('Selected region could not be found.');
      }

      const saved = await saveRegionMap(saveRegionId, payload);
      if (selectedRegionIdRef.current !== saveRegionId) return;

      if (editVersionRef.current !== saveEditVersion) {
        setMap(current => mergeSavedMapVersion(current, saved));
        setIsDirty(true);
        setSaveFeedback({
          type: 'warning',
          message: 'The earlier snapshot was saved. Newer edits remain unsaved.',
        });
        return;
      }

      if (saved) {
        setMap(ensureRegionMap(
          saved,
          field(saved, 'width', 'Width') ?? map.width,
          field(saved, 'height', 'Height') ?? map.height,
        ));
      }
      setIsDirty(false);
      setSaveFeedback({
        type: 'success',
        message: 'Map, entrances, exits, and resources saved.',
      });
    } catch (error) {
      console.error('Failed to save region map', error);
      if (selectedRegionIdRef.current !== saveRegionId) return;
      setSaveFeedback({
        type: 'error',
        message: error?.message || 'The map could not be saved. Reload and try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePlaceMarker = (x, y) => {
    if (!placementMode) return;
    if (placementMode.startsWith('exit:')) {
      const exitKey = placementMode.slice('exit:'.length);
      const exitIndex = map.exits.findIndex(exit => exit.key === exitKey);
      if (exitIndex < 0) {
        setPlacementMode('');
        return;
      }
      setMap(prev => ({
        ...prev,
        exits: prev.exits.map((entry, index) => (
          index === exitIndex ? { ...entry, x, y } : entry
        )),
      }));
    } else if (placementMode === 'camp') {
      setMap(prev => ({
        ...prev,
        campEntranceX: x,
        campEntranceY: y,
      }));
    } else if (placementMode === 'spawn') {
      setMap(prev => ({
        ...prev,
        spawnLocationX: x,
        spawnLocationY: y,
      }));
    } else if (placementMode === 'fixed-encounter') {
      setMap(prev => ({
        ...prev,
        championPlacement: prev.championPlacement == null
          ? null
          : { ...prev.championPlacement, x, y },
      }));
    } else if (placementMode.startsWith('resource:')) {
      const placementId = placementMode.slice('resource:'.length);
      setMap(prev => ({
        ...prev,
        resourceNodes: prev.resourceNodes.map(
          placement => placement.placementId === placementId
            ? { ...placement, x, y }
            : placement,
        ),
      }));
    } else if (placementMode.startsWith('loot:')) {
      const placementId = placementMode.slice('loot:'.length);
      setMap(prev => ({
        ...prev,
        lootContainerPlacements: prev.lootContainerPlacements.map(
          placement => placement.placementId === placementId
            ? { ...placement, x, y }
            : placement,
        ),
      }));
    }
    setPlacementMode('');
    markDirty();
  };

  const handleAddExit = () => {
    const key = nextMapEntryKey('exit', map.exits);
    const target = regions.find(region => (
      field(region, 'id', 'Id') !== selectedRegionId
    ));
    setMap(prev => ({
      ...prev,
      exits: [...prev.exits, {
        key,
        label: '',
        x: 0,
        y: 0,
        direction: '',
        targetRegionId: field(target, 'id', 'Id') || '',
        targetExitKey: '',
        requiredRunUnlockKey: '',
        tollItemId: '',
        tollQuantity: 0,
        tollPaidRunUnlockKey: '',
        enabled: false,
      }],
    }));
    markDirty();
  };

  const handleChangeExit = (index, patch) => {
    const previousKey = map.exits[index]?.key;
    setMap(prev => ({
      ...prev,
      exits: prev.exits.map((exit, exitIndex) => (
        exitIndex === index ? { ...exit, ...patch } : exit
      )),
    }));
    if (
      patch.key
      && placementMode === `exit:${previousKey}`
    ) {
      setPlacementMode(`exit:${patch.key}`);
    }
    markDirty();
  };

  const handleRemoveExit = (index) => {
    const removedKey = map.exits[index]?.key;
    setMap(prev => ({
      ...prev,
      exits: prev.exits.filter((_, exitIndex) => exitIndex !== index),
    }));
    if (placementMode === `exit:${removedKey}`) setPlacementMode('');
    markDirty();
  };

  const handleAddResourceRule = () => {
    const key = nextMapEntryKey('resource_rule', map.resourceSpawnRules);
    const item = spawnableItems[0];
    setMap(prev => ({
      ...prev,
      resourceSpawnRules: [
        ...prev.resourceSpawnRules,
        normalizeResourceSpawnRule({
          key,
          itemId: field(item, 'itemId', 'ItemId') || '',
          minimumStack: 1,
          maximumStack: 1,
          maximumCharges: 1,
          respawnSeconds: 60,
          count: 1,
          minimumSpacing: 1,
          excludeSpawnRadius: 1,
          rotationCycleSeconds: 86400,
          enabled: true,
          near: null,
        }),
      ],
    }));
    markDirty();
  };

  const handleAddResourcePlacement = () => {
    const ids = new Set(map.resourceNodes.map(entry => entry.placementId));
    let ordinal = 1;
    while (ids.has(`resource_placement_${ordinal}`)) ordinal += 1;
    const placementId = `resource_placement_${ordinal}`;
    const item = spawnableItems[0];
    setMap(prev => ({
      ...prev,
      resourceNodes: [
        ...prev.resourceNodes,
        normalizeResourcePlacement({
          placementId,
          itemId: field(item, 'itemId', 'ItemId') || '',
          x: prev.spawnLocationX,
          y: prev.spawnLocationY,
        }),
      ],
    }));
    setPlacementMode(`resource:${placementId}`);
    markDirty();
  };

  const handleChangeResourcePlacement = (index, patch) => {
    const previousId = map.resourceNodes[index]?.placementId;
    setMap(prev => ({
      ...prev,
      resourceNodes: prev.resourceNodes.map((placement, placementIndex) => (
        placementIndex === index ? { ...placement, ...patch } : placement
      )),
    }));
    if (patch.placementId && placementMode === `resource:${previousId}`) {
      setPlacementMode(`resource:${patch.placementId}`);
    }
    markDirty();
  };

  const handleRemoveResourcePlacement = (index) => {
    const id = map.resourceNodes[index]?.placementId;
    setMap(prev => ({
      ...prev,
      resourceNodes: prev.resourceNodes.filter(
        (_, placementIndex) => placementIndex !== index,
      ),
    }));
    if (placementMode === `resource:${id}`) setPlacementMode('');
    markDirty();
  };

  const handleChangeResourceRule = (index, patch) => {
    setMap(prev => ({
      ...prev,
      resourceSpawnRules: prev.resourceSpawnRules.map((rule, ruleIndex) => (
        ruleIndex === index ? { ...rule, ...patch } : rule
      )),
    }));
    markDirty();
  };

  const handleRemoveResourceRule = (index) => {
    setMap(prev => ({
      ...prev,
      resourceSpawnRules: prev.resourceSpawnRules.filter(
        (_, ruleIndex) => ruleIndex !== index,
      ),
    }));
    markDirty();
  };

  const handlePreviewResources = async () => {
    if (!selectedRegionId) return;
    setIsPreviewing(true);
    setPreviewError('');
    try {
      const preview = await previewRegionResourceSpawns(
        selectedRegionId,
        mapPayload(map),
      );
      setResourcePreview(preview);
    } catch (error) {
      setResourcePreview(null);
      setPreviewError(
        error?.message || 'The resource preview could not be calculated.',
      );
    } finally {
      setIsPreviewing(false);
    }
  };

  const nextLootPlacementId = () => {
    const ids = new Set(
      map.lootContainerPlacements.map(entry => entry.placementId),
    );
    let ordinal = 1;
    while (ids.has(`loot_placement_${ordinal}`)) ordinal += 1;
    return `loot_placement_${ordinal}`;
  };

  const handleAddLootPlacement = () => {
    const placementId = nextLootPlacementId();
    const typeId = field(lootContainerTypes[0], 'id', 'Id') || '';
    setMap(prev => ({
      ...prev,
      lootContainerPlacements: [
        ...prev.lootContainerPlacements,
        {
          placementId,
          containerTypeId: typeId,
          x: prev.spawnLocationX,
          y: prev.spawnLocationY,
        },
      ],
    }));
    setPlacementMode(`loot:${placementId}`);
    markDirty();
  };

  const handleChangeLootPlacement = (index, patch) => {
    setMap(prev => ({
      ...prev,
      lootContainerPlacements: prev.lootContainerPlacements.map(
        (placement, placementIndex) => (
          placementIndex === index ? { ...placement, ...patch } : placement
        ),
      ),
    }));
    markDirty();
  };

  const handleRemoveLootPlacement = (index) => {
    const id = map.lootContainerPlacements[index]?.placementId;
    setMap(prev => ({
      ...prev,
      lootContainerPlacements: prev.lootContainerPlacements.filter(
        (_, placementIndex) => placementIndex !== index,
      ),
    }));
    if (placementMode === `loot:${id}`) setPlacementMode('');
    markDirty();
  };

  const handleAddLootRule = () => {
    const key = nextMapEntryKey(
      'loot_rule',
      map.lootContainerSpawnRules,
    );
    setMap(prev => ({
      ...prev,
      lootContainerSpawnRules: [
        ...prev.lootContainerSpawnRules,
        normalizeLootContainerSpawnRule({
          key,
          containerTypeId:
            field(lootContainerTypes[0], 'id', 'Id') || '',
          count: 1,
          allowedBaseKeys: [activeBaseTile],
          minimumSpacing: 1,
          excludeSpawnRadius: 1,
          enabled: true,
        }),
      ],
    }));
    markDirty();
  };

  const handleChangeLootRule = (index, patch) => {
    setMap(prev => ({
      ...prev,
      lootContainerSpawnRules: prev.lootContainerSpawnRules.map(
        (rule, ruleIndex) => (
          ruleIndex === index ? { ...rule, ...patch } : rule
        ),
      ),
    }));
    markDirty();
  };

  const handleRemoveLootRule = (index) => {
    setMap(prev => ({
      ...prev,
      lootContainerSpawnRules: prev.lootContainerSpawnRules.filter(
        (_, ruleIndex) => ruleIndex !== index,
      ),
    }));
    markDirty();
  };

  const handlePreviewLoot = async () => {
    if (!selectedRegionId) return;
    setIsPreviewingLoot(true);
    setLootPreviewError('');
    try {
      setLootPreview(await previewRegionLootContainerSpawns(
        selectedRegionId,
        mapPayload(map),
      ));
    } catch (error) {
      setLootPreview(null);
      setLootPreviewError(
        error?.message || 'The loot preview could not be calculated.',
      );
    } finally {
      setIsPreviewingLoot(false);
    }
  };

  const handlePreviewEnemies = async () => {
    if (!selectedRegionId) return;
    setIsPreviewingEnemies(true);
    setEnemyPreviewError('');
    try {
      const preview = await previewRegionEnemySpawns(
        selectedRegionId,
        mapPayload(map),
      );
      setEnemyPreview(preview);
      const placements = field(preview, 'placements', 'Placements') || [];
      const firstPlacement = placements[0];
      const definitions = field(preview, 'enemies', 'Enemies') || [];
      setSelectedEnemyId(
        field(firstPlacement, 'enemyId', 'EnemyId')
          || field(definitions[0], 'enemyId', 'EnemyId')
          || '',
      );
      setSelectedEnemyInstanceId(
        field(firstPlacement, 'instanceId', 'InstanceId') || '',
      );
    } catch (error) {
      setEnemyPreview(null);
      setEnemyPreviewError(
        error?.message || 'The enemy preview could not be calculated.',
      );
    } finally {
      setIsPreviewingEnemies(false);
    }
  };

  const baseTiles = useMemo(
    () => tileTypes.map((t) => ({
      id: t.key || t.id,
      label: t.label || t.name || t.id,
      fallbackColor: t.fallbackColor,
      imageId: t.imageId,
    })),
    [tileTypes],
  );

  const detailPalette = useMemo(
    () => detailOptions.map((detail) => ({
      id: detail.key || detail.id,
      label: detail.label || detail.name || detail.id,
      fallbackColor: detail.fallbackColor,
      imageId: detail.imageId,
      type: detail.type,
    })),
    [detailOptions],
  );

  const regionEnemies = useMemo(() => {
    const typesById = new Map(enemyTypes.map(type => [
      field(type, 'id', 'Id'),
      type,
    ]));
    const championEnemyId = field(
      map.championPlacement,
      'enemyId',
      'EnemyId',
    );
    return enemies
      .filter(enemy => field(enemy, 'regionId', 'RegionId')
        === selectedRegionId)
      .map((enemy) => {
        const enemyId = field(enemy, 'id', 'Id');
        const type = typesById.get(field(enemy, 'enemyType', 'EnemyType'));
        const spawnWeight = field(
          enemy,
          'spawnWeightOverride',
          'SpawnWeightOverride',
        ) ?? field(type, 'spawnchance', 'Spawnchance') ?? 0;
        return {
          ...enemy,
          id: enemyId,
          imageId: field(enemy, 'imageId', 'ImageId') || '',
          isChampion: enemyId === championEnemyId,
          name: field(enemy, 'name', 'Name') || enemyId,
          spawnWeight,
        };
      })
      .filter(enemy => enemy.isChampion || enemy.spawnWeight > 0)
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [enemies, enemyTypes, map.championPlacement, selectedRegionId]);

  const fixedEncounterEnemies = useMemo(() => {
    const typesById = new Map(enemyTypes.map(type => [
      field(type, 'id', 'Id'),
      field(type, 'name', 'Name'),
    ]));
    return enemies
      .filter(enemy => field(enemy, 'regionId', 'RegionId')
        === selectedRegionId)
      .map(enemy => ({
        id: field(enemy, 'id', 'Id'),
        name: field(enemy, 'name', 'Name'),
        typeName: typesById.get(field(enemy, 'enemyType', 'EnemyType')),
      }))
      .filter(enemy => ['Champion', 'Boss'].includes(enemy.typeName))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [enemies, enemyTypes, selectedRegionId]);

  const fixedEncounterValidation = map.championPlacement == null
    ? ''
    : !map.championPlacement.placementId.trim()
      ? 'Enter a stable placement ID.'
      : !fixedEncounterEnemies.some(enemy => (
        enemy.id === map.championPlacement.enemyId
      ))
      ? 'Choose a Champion or Boss enemy.'
      : map.championPlacement.x < 0
        || map.championPlacement.x >= map.width
        || map.championPlacement.y < 0
        || map.championPlacement.y >= map.height
        ? 'Place the fixed encounter inside the map.'
        : map.championPlacement.repeatAfterCooldown
          ? map.championPlacement.rewardCooldownSeconds <= 0
            ? 'Repeatable fixed encounters require a positive cooldown.'
            : map.championPlacement.runUnlockKey
              ? 'Repeatable fixed encounters cannot grant a run unlock.'
              : ''
          : !map.championPlacement.runUnlockKey
            ? 'Once-per-run fixed encounters require a run unlock key.'
            : '';

  const handleAddFixedEncounter = () => {
    const enemy = fixedEncounterEnemies[0];
    if (!enemy) return;
    setMap(prev => ({
      ...prev,
      championPlacement: {
        placementId: `fixed-${selectedRegionId}`,
        enemyId: enemy.id,
        x: prev.spawnLocationX,
        y: prev.spawnLocationY,
        runUnlockKey: '',
        repeatAfterCooldown: true,
        rewardCooldownSeconds: 3600,
        guaranteedDropCount: 0,
        bonusDropChancePercent: 0,
      },
    }));
    markDirty();
  };

  const handleChangeFixedEncounter = (patch) => {
    setMap(prev => ({
      ...prev,
      championPlacement: prev.championPlacement == null
        ? null
        : { ...prev.championPlacement, ...patch },
    }));
    markDirty();
  };

  const handleRemoveFixedEncounter = () => {
    setMap(prev => ({ ...prev, championPlacement: null }));
    setPlacementMode('');
    markDirty();
  };

  const enemyPreviewPlacements = field(
    enemyPreview,
    'placements',
    'Placements',
  ) || [];
  const selectedEnemyDefinition = (
    field(enemyPreview, 'enemies', 'Enemies') || []
  ).find(definition => field(definition, 'enemyId', 'EnemyId')
    === selectedEnemyId);
  const selectedEnemyPlacement = enemyPreviewPlacements.find(placement =>
    field(placement, 'instanceId', 'InstanceId')
      === selectedEnemyInstanceId);

  const handleSelectEnemy = (enemyId) => {
    setSelectedEnemyId(enemyId);
    const placement = enemyPreviewPlacements.find(candidate =>
      field(candidate, 'enemyId', 'EnemyId') === enemyId);
    setSelectedEnemyInstanceId(
      field(placement, 'instanceId', 'InstanceId') || '',
    );
  };

  const handleSelectEnemyPlacement = (placement) => {
    setSelectedEnemyId(field(placement, 'enemyId', 'EnemyId') || '');
    setSelectedEnemyInstanceId(
      field(placement, 'instanceId', 'InstanceId') || '',
    );
  };

  const handleTabKeyDown = (event, index) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? SIDEBAR_TABS.length - 1
        : (index + (event.key === 'ArrowRight' ? 1 : -1) + SIDEBAR_TABS.length)
          % SIDEBAR_TABS.length;
    setActiveSidebarTab(SIDEBAR_TABS[nextIndex].id);
    event.currentTarget.parentElement.children[nextIndex].focus();
  };

  return (
    <div className="admin-world-editor">
      <div className="admin-world-editor__sidebar">
        <label className="form-control" htmlFor="world-editor-region">
          <span>Region</span>
          <select
            disabled={isLoadingMap || isSaving}
            id="world-editor-region"
            value={selectedRegionId}
            onChange={(event) => {
              const nextRegionId = event.target.value;
              selectedRegionIdRef.current = nextRegionId;
              editVersionRef.current += 1;
              setSelectedRegionId(nextRegionId);
            }}
          >
            {regions.map((region) => (
              <option
                key={field(region, 'id', 'Id')}
                value={field(region, 'id', 'Id')}
              >
                {field(region, 'name', 'Name')
                  || field(region, 'id', 'Id')}
              </option>
            ))}
          </select>
        </label>
        {isLoadingMap && <p className="world-editor-loading" role="status">Loading map…</p>}
        <div className="world-editor-tabs" role="tablist" aria-label="Map editor settings">
          {SIDEBAR_TABS.map((tab, index) => (
            <button
              aria-controls={`world-editor-panel-${tab.id}`}
              aria-selected={activeSidebarTab === tab.id}
              className={activeSidebarTab === tab.id ? 'is-active' : ''}
              id={`world-editor-tab-${tab.id}`}
              key={tab.id}
              onClick={() => setActiveSidebarTab(tab.id)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
              role="tab"
              tabIndex={activeSidebarTab === tab.id ? 0 : -1}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div
          aria-labelledby="world-editor-tab-terrain"
          className="world-editor-tab-panel"
          hidden={activeSidebarTab !== 'terrain'}
          id="world-editor-panel-terrain"
          role="tabpanel"
        >
          <div className="form-control">
            <span>Size</span>
            <small>Map size is stored with this world map.</small>
            <div className="size-inputs">
              <label>
                <span>Width</span>
                <input type="number" min="1" max="200" value={map.width} onChange={(e) => handleResize(e.target.value, map.height)} />
              </label>
              <span>x</span>
              <label>
                <span>Height</span>
                <input type="number" min="1" max="200" value={map.height} onChange={(e) => handleResize(map.width, e.target.value)} />
              </label>
            </div>
          </div>
          <fieldset className="world-editor-paint-layer">
            <legend>Paint layer</legend>
            <label className={paintLayer === 'base' ? 'is-active' : ''}>
              <input
                checked={paintLayer === 'base'}
                name="world-editor-paint-layer"
                onChange={() => setPaintLayer('base')}
                type="radio"
              />
              Base tiles
            </label>
            <label className={paintLayer === 'detail' ? 'is-active' : ''}>
              <input
                checked={paintLayer === 'detail'}
                name="world-editor-paint-layer"
                onChange={() => setPaintLayer('detail')}
                type="radio"
              />
              Details
            </label>
            <label className={paintLayer === 'walkability' ? 'is-active' : ''}>
              <input
                checked={paintLayer === 'walkability'}
                name="world-editor-paint-layer"
                onChange={() => {
                  setPaintLayer('walkability');
                  setShowWalkabilityOverlay(true);
                }}
                type="radio"
              />
              Walkability
            </label>
          </fieldset>
          <details className="world-editor-palette" open>
            <summary>Terrain Palette</summary>
            <div className="palette">
              <h3>Base Tiles</h3>
              <div className="palette-grid">
                {baseTiles.map((tile) => {
                  const imageUrl = tile.imageId ? getImageUrl(tile.imageId, MEDIA_VARIANTS.TILE) : null;
                  const previewStyle = {
                    backgroundColor: tile.fallbackColor || '#6b7280',
                    backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
                  };
                  return (
                    <button
                      key={tile.id}
                      className={`palette-tile ${tile.id === activeBaseTile ? 'is-active' : ''}`}
                      type="button"
                      onClick={() => {
                        setActiveBaseTile(tile.id);
                        setPaintLayer('base');
                      }}
                    >
                      <span className="palette-preview palette-preview--tile" style={previewStyle} />
                      <span className="palette-label">{tile.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="palette">
              <h3>Details</h3>
              <div className="palette-grid">
                {detailPalette.map((detail) => {
                  const isNone = detail.id === 'none';
                  const imageUrl = !isNone && detail.imageId
                    ? getImageUrl(detail.imageId, MEDIA_VARIANTS.TILE_DETAIL)
                    : null;
                  const previewClassName = ['palette-preview', 'palette-preview--detail'];
                  if (imageUrl) {
                    previewClassName.push('palette-preview--image');
                  }
                  if (isNone) {
                    previewClassName.push('palette-preview--none');
                  }

                  const previewStyle = imageUrl
                    ? { backgroundImage: `url(${imageUrl})` }
                    : isNone
                      ? { backgroundColor: 'transparent' }
                      : { backgroundColor: detail.fallbackColor || 'rgba(0,0,0,0.35)' };

                  return (
                    <button
                      key={detail.id}
                      className={`palette-detail ${detail.id === activeDetail ? 'is-active' : ''}`}
                      type="button"
                      onClick={() => {
                        setActiveDetail(detail.id);
                        setPaintLayer('detail');
                      }}
                    >
                      <span className={previewClassName.join(' ')} style={previewStyle} />
                      <span className="palette-label">{detail.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="palette">
              <h3>Walkability Override</h3>
              <div className="palette-grid">
                {[
                  { label: 'Inherit Base', value: null, className: 'inherit' },
                  { label: 'Walkable', value: true, className: 'walkable' },
                  { label: 'Blocked', value: false, className: 'blocked' },
                ].map(option => (
                  <button
                    className={`palette-tile ${activeWalkableOverride === option.value ? 'is-active' : ''}`}
                    key={option.label}
                    onClick={() => {
                      setActiveWalkableOverride(option.value);
                      setPaintLayer('walkability');
                      setShowWalkabilityOverlay(true);
                    }}
                    type="button"
                  >
                    <span className={`palette-preview palette-preview--walkability palette-preview--${option.className}`} />
                    <span className="palette-label">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </details>
        </div>

        <div
          aria-labelledby="world-editor-tab-travel"
          className="world-editor-tab-panel"
          hidden={activeSidebarTab !== 'travel'}
          id="world-editor-panel-travel"
          role="tabpanel"
        >
          <section className="world-editor-section" aria-labelledby="world-markers-title">
            <div className="world-editor-section__heading">
              <div>
                <h3 id="world-markers-title">Map Entrances</h3>
                <p>Place the player spawn and the entrance to the private camp.</p>
              </div>
            </div>
            <div className="world-editor-marker-control">
              <div>
                <strong>Player Spawn</strong>
                <div className="world-editor-inline-coordinates">
                  <label>
                    <span>X</span>
                    <input
                      aria-label="Player spawn X"
                      max={map.width - 1}
                      min="0"
                      type="number"
                      value={map.spawnLocationX}
                      onChange={(event) => {
                        const value = Math.min(
                          map.width - 1,
                          Math.max(0, Number(event.target.value) || 0),
                        );
                        setMap(prev => ({ ...prev, spawnLocationX: value }));
                        markDirty();
                      }}
                    />
                  </label>
                  <label>
                    <span>Y</span>
                    <input
                      aria-label="Player spawn Y"
                      max={map.height - 1}
                      min="0"
                      type="number"
                      value={map.spawnLocationY}
                      onChange={(event) => {
                        const value = Math.min(
                          map.height - 1,
                          Math.max(0, Number(event.target.value) || 0),
                        );
                        setMap(prev => ({ ...prev, spawnLocationY: value }));
                        markDirty();
                      }}
                    />
                  </label>
                </div>
              </div>
              <button
                className={`button-secondary ${placementMode === 'spawn' ? 'is-active' : ''}`}
                type="button"
                onClick={() => setPlacementMode(prev => (
                  prev === 'spawn' ? '' : 'spawn'
                ))}
              >
                {placementMode === 'spawn' ? 'Click a tile…' : 'Place Spawn'}
              </button>
            </div>
            <div className="world-editor-marker-control">
              <div>
                <strong>Camp Entrance</strong>
                <div className="world-editor-inline-coordinates">
                  <label>
                    <span>X</span>
                    <input
                      aria-label="Camp entrance X"
                      max={map.width - 1}
                      min="0"
                      placeholder="—"
                      type="number"
                      value={map.campEntranceX ?? ''}
                      onChange={(event) => {
                        const value = event.target.value === ''
                          ? null
                          : Math.min(
                            map.width - 1,
                            Math.max(0, Number(event.target.value) || 0),
                          );
                        setMap(prev => ({ ...prev, campEntranceX: value }));
                        markDirty();
                      }}
                    />
                  </label>
                  <label>
                    <span>Y</span>
                    <input
                      aria-label="Camp entrance Y"
                      max={map.height - 1}
                      min="0"
                      placeholder="—"
                      type="number"
                      value={map.campEntranceY ?? ''}
                      onChange={(event) => {
                        const value = event.target.value === ''
                          ? null
                          : Math.min(
                            map.height - 1,
                            Math.max(0, Number(event.target.value) || 0),
                          );
                        setMap(prev => ({ ...prev, campEntranceY: value }));
                        markDirty();
                      }}
                    />
                  </label>
                </div>
              </div>
              <div className="world-editor-marker-control__actions">
                <button
                  className={`button-secondary ${placementMode === 'camp' ? 'is-active' : ''}`}
                  type="button"
                  onClick={() => setPlacementMode(prev => (
                    prev === 'camp' ? '' : 'camp'
                  ))}
                >
                  {placementMode === 'camp' ? 'Click a tile…' : 'Place Camp'}
                </button>
                <button
                  className="button-secondary"
                  disabled={map.campEntranceX === null && map.campEntranceY === null}
                  type="button"
                  onClick={() => {
                    setMap(prev => ({
                      ...prev,
                      campEntranceX: null,
                      campEntranceY: null,
                    }));
                    setPlacementMode('');
                    markDirty();
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </section>

          <RegionExitEditor
            currentRegionId={selectedRegionId}
            exits={map.exits}
            onAdd={handleAddExit}
            onChange={handleChangeExit}
            onPlace={(exitKey) => setPlacementMode(prev => (
              prev === `exit:${exitKey}` ? '' : `exit:${exitKey}`
            ))}
            onRemove={handleRemoveExit}
            placingExitKey={
              placementMode.startsWith('exit:')
                ? placementMode.slice('exit:'.length)
                : ''
            }
            regions={regions}
            spawnableItems={spawnableItems}
          />
        </div>

        <div
          aria-labelledby="world-editor-tab-enemies"
          className="world-editor-tab-panel"
          hidden={activeSidebarTab !== 'enemies'}
          id="world-editor-panel-enemies"
          role="tabpanel"
        >
          <EnemySpawnPreviewEditor
            enemies={regionEnemies}
            error={enemyCatalogError || enemyPreviewError}
            fixedEncounter={map.championPlacement}
            fixedEncounterEnemies={fixedEncounterEnemies}
            fixedEncounterValidation={fixedEncounterValidation}
            getImageUrl={getImageUrl}
            mapHeight={map.height}
            mapWidth={map.width}
            onAddFixedEncounter={handleAddFixedEncounter}
            onChangeFixedEncounter={handleChangeFixedEncounter}
            onPlaceFixedEncounter={() => setPlacementMode(prev => (
              prev === 'fixed-encounter' ? '' : 'fixed-encounter'
            ))}
            onPreview={handlePreviewEnemies}
            onRemoveFixedEncounter={handleRemoveFixedEncounter}
            onSelectEnemy={handleSelectEnemy}
            placingFixedEncounter={placementMode === 'fixed-encounter'}
            preview={enemyPreview}
            previewBusy={isPreviewingEnemies}
            selectedEnemyId={selectedEnemyId}
          />
        </div>

        <div
          aria-labelledby="world-editor-tab-resources"
          className="world-editor-tab-panel"
          hidden={activeSidebarTab !== 'resources'}
          id="world-editor-panel-resources"
          role="tabpanel"
        >
          <ResourceSpawnRulesEditor
            baseTiles={baseTiles}
            detailTiles={detailPalette}
            getImageUrl={getImageUrl}
            onAdd={handleAddResourceRule}
            onAddPlacement={handleAddResourcePlacement}
            onChange={handleChangeResourceRule}
            onChangePlacement={handleChangeResourcePlacement}
            onPlace={placementId => setPlacementMode(prev => (
              prev === `resource:${placementId}`
                ? ''
                : `resource:${placementId}`
            ))}
            onPreview={handlePreviewResources}
            onRemove={handleRemoveResourceRule}
            onRemovePlacement={handleRemoveResourcePlacement}
            placementMode={placementMode}
            placements={map.resourceNodes}
            preview={resourcePreview}
            previewBusy={isPreviewing}
            previewError={previewError}
            rules={map.resourceSpawnRules}
            spawnableItems={spawnableItems}
            spawnableItemsError={spawnableItemsError}
          />
        </div>

        <div
          aria-labelledby="world-editor-tab-loot"
          className="world-editor-tab-panel"
          hidden={activeSidebarTab !== 'loot'}
          id="world-editor-panel-loot"
          role="tabpanel"
        >
          <LootContainerRulesEditor
            baseTiles={baseTiles}
            containerTypes={lootContainerTypes}
            detailTiles={detailPalette}
            getImageUrl={getImageUrl}
            onAddPlacement={handleAddLootPlacement}
            onAddRule={handleAddLootRule}
            onChangePlacement={handleChangeLootPlacement}
            onChangeRule={handleChangeLootRule}
            onPlace={placementId => setPlacementMode(prev => (
              prev === `loot:${placementId}` ? '' : `loot:${placementId}`
            ))}
            onPreview={handlePreviewLoot}
            onRemovePlacement={handleRemoveLootPlacement}
            onRemoveRule={handleRemoveLootRule}
            placementMode={placementMode}
            placements={map.lootContainerPlacements}
            preview={lootPreview}
            previewBusy={isPreviewingLoot}
            previewError={lootPreviewError}
            rules={map.lootContainerSpawnRules}
          />
        </div>

        <div className="world-editor-save">
          <button
            className="button-primary"
            type="button"
            onClick={handleSave}
            disabled={!isDirty || isSaving || isLoadingMap || Boolean(fixedEncounterValidation)}
          >
          {isSaving ? 'Saving…' : 'Save Map'}
          </button>
          {saveFeedback && (
            <p
              className={`world-editor-feedback world-editor-feedback--${saveFeedback.type}`}
              role={saveFeedback.type === 'error' ? 'alert' : 'status'}
            >
              {saveFeedback.message}
            </p>
          )}
        </div>
      </div>
      <div className="admin-world-editor__canvas" aria-busy={isLoadingMap}>
        <div className="world-editor-map-legend" aria-label="Map marker legend">
          <span><i className="legend-swatch legend-swatch--spawn" /> Spawn</span>
          <span><i className="legend-swatch legend-swatch--camp" /> Camp</span>
          <span><i className="legend-swatch legend-swatch--exit" /> Exit</span>
          <span><i className="legend-swatch legend-swatch--resource" /> Resource</span>
          <span><i className="legend-swatch legend-swatch--loot" /> Loot</span>
          {enemyPreviewPlacements.length ? (
            <span><i className="legend-swatch legend-swatch--enemy" /> Enemy preview</span>
          ) : null}
          {selectedEnemyId ? (
            <>
              <span><i className="legend-swatch legend-swatch--enemy-spawn" /> Enemy spawn area</span>
              {selectedEnemyPlacement ? (
                <span><i className="legend-swatch legend-swatch--enemy-roaming" /> Patrol reach</span>
              ) : null}
            </>
          ) : null}
          <label className="world-editor-map-legend__toggle">
            <input
              checked={showFogPreview}
              onChange={(event) => setShowFogPreview(event.target.checked)}
              type="checkbox"
            />
            Fog preview
          </label>
          <label className="world-editor-map-legend__toggle">
            <input
              checked={showWalkabilityOverlay}
              onChange={(event) => setShowWalkabilityOverlay(event.target.checked)}
              type="checkbox"
            />
            Walkability overlay
          </label>
          {showWalkabilityOverlay ? (
            <>
              <span><i className="legend-swatch legend-swatch--walkable" /> Walkable</span>
              <span><i className="legend-swatch legend-swatch--blocked" /> Blocked</span>
            </>
          ) : null}
        </div>
        <div className="worldeditor-map-frame">
          {[
            ['top', 'row', map.height],
            ['left', 'column', map.width],
            ['right', 'column', map.width],
            ['bottom', 'row', map.height],
          ].map(([edge, line, size]) => (
            <div
              aria-label={`${edge} ${line} controls`}
              className={`worldeditor-map-edge worldeditor-map-edge--${edge}`}
              key={edge}
              role="group"
            >
              <button
                aria-label={`Remove terrain ${line} at ${edge}`}
                className="button-secondary"
                disabled={size <= 1}
                onClick={() => handleResizeMapEdge(edge, -1)}
                title={`Remove the ${edge} terrain ${line}.`}
                type="button"
              >
                −
              </button>
              <span>{line === 'row' ? 'Row' : 'Col'}</span>
              <button
                aria-label={`Add terrain ${line} at ${edge}`}
                className="button-secondary"
                disabled={size >= 200}
                onClick={() => handleResizeMapEdge(edge, 1)}
                title={`Duplicate the ${edge} terrain ${line}.`}
                type="button"
              >
                +
              </button>
            </div>
          ))}
          <RegionMapEditor
            map={map}
            activeBaseTile={activeBaseTile}
            activeDetail={activeDetail}
            activeWalkableOverride={activeWalkableOverride}
            tileTypes={tileTypes}
            detailTypes={detailOptions}
            onPaint={handlePaint}
            spawnLocation={{
              x: map.spawnLocationX,
              y: map.spawnLocationY,
            }}
            campEntrance={
              map.campEntranceX === null || map.campEntranceY === null
                ? null
                : { x: map.campEntranceX, y: map.campEntranceY }
            }
            exits={map.exits}
            placementMode={placementMode}
            onPlaceMarker={handlePlaceMarker}
            showWorldShroud={showFogPreview}
            showWalkabilityOverlay={showWalkabilityOverlay}
            resourceNodes={
              field(
                resourcePreview,
                'effectivePlacements',
                'EffectivePlacements',
              ) || map.resourceNodes
            }
            resourceItems={spawnableItems}
            lootContainers={
              field(
                lootPreview,
                'effectivePlacements',
                'EffectivePlacements',
              ) || map.lootContainerPlacements
            }
            lootContainerTypes={lootContainerTypes}
            enemyPreviewPlacements={enemyPreviewPlacements}
            enemySpawnTileIndexes={field(
              selectedEnemyDefinition,
              'spawnTileIndexes',
              'SpawnTileIndexes',
            ) || []}
            enemyRoamingTileIndexes={field(
              selectedEnemyPlacement,
              'roamingTileIndexes',
              'RoamingTileIndexes',
            ) || []}
            onSelectEnemyPreview={handleSelectEnemyPlacement}
            selectedEnemyPreviewId={selectedEnemyInstanceId}
          />
        </div>
      </div>
    </div>
  );
}

export default AdminWorldEditor;
